import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import * as Sentry from '@sentry/angular';
import { ScreenEnum } from '../enums/screens.enum';
import {
  Dataset,
  INITIAL_SELECTED_PERIOD,
  SiDetailsQueryParams
} from '../models/crd-state.interface';
import { PrimengExportsModule } from '../primeng-exports.module';
import { AuthService } from '../services/auth.service';
import { DatasetPreferenceService } from '../services/dataset-preference.service';
import { PosthogService } from '../services/posthog.service';
import { CrdStateService } from '../services/state-service/crd-state.service';
import { UtilityService } from '../services/utility.service';
import { UserStateService } from '../services/state-service/user-state.service';
import { UserInfo } from '../models/user-state.interface';
import { resolveDefaultDataset } from '../utils/default-dataset.util';
import { combineLatest, filter, merge, take, timer } from 'rxjs';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';

/**
 * How long dataset selection waits for user info before giving up on the org
 * match and falling back to the first dataset, so a stalled request cannot
 * leave the dashboard empty.
 */
const USER_INFO_GATE_TIMEOUT_MS = 5000;

@Component({
  selector: 'app-crd-shell',
  imports: [
    CommonModule,
    EmptyStateComponent,
    PrimengExportsModule,
    RouterModule,
    TranslocoPipe
  ],
  templateUrl: './crd-shell.component.html',
  styleUrls: ['./crd-shell.component.scss']
})
export class CrdShellComponent implements OnInit, AfterViewChecked {
  readonly container = viewChild<ElementRef>('container');
  private readonly destroyRef = inject(DestroyRef);
  private readonly datasetPreferenceService = inject(DatasetPreferenceService);
  get canAssignToken(): boolean {
    return (
      this.authService.userInfoToken === 'null' ||
      !this.authService.userInfoToken ||
      !!this.route.snapshot.params['token']
    );
  }
  screens = ScreenEnum;

  get hasRouteParams(): boolean {
    return (
      !!this.route.snapshot.params['crd'] &&
      !!this.route.snapshot.params['plantationTable']
    );
  }
  constructor(
    public crdStateService: CrdStateService,
    private readonly route: ActivatedRoute,
    private readonly authService: AuthService,
    private readonly utilityService: UtilityService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly userStateService: UserStateService,
    private readonly posthogService: PosthogService
  ) {}

  ngOnInit(): void {
    this.initializeGlobal();
  }

  ngAfterViewChecked() {
    this.emitContentHeight();
  }

  initializeGlobal() {
    if (this.route.snapshot.params['token']) {
      this.initializeToken();
      this.initializeIsSiLinking();
      this.userStateService.getUserInfo();
      this.initializeApiParams();
      this.crdStateService.setSelectedPeriod(INITIAL_SELECTED_PERIOD);
      this.initializePendo();
      this.initializeSentryUser();
      this.initializePosthogUser();
    }
  }

  emitContentHeight() {
    const container = this.container()?.nativeElement;
    if (!container) return;
    const divHeight = container.offsetHeight;
    const remValue = divHeight / 14;
    this.utilityService.emitPropToParent('contentHeight', remValue);
  }

  initializeIsSiLinking() {
    this.route.queryParams.subscribe((params) => {
      if (params['siNumber']) {
        this.crdStateService.setSiDetails(params as SiDetailsQueryParams);
        this.crdStateService.setCurrentScreen(ScreenEnum.SI_LINKING);
        this.router
          .navigate(['si-linking'], {
            relativeTo: this.activatedRoute
          })
          .catch((error: unknown) => {
            console.error('Navigation failed.', error);
          });
      }
    });
  }

  initializeApiParams() {
    this.crdStateService.getDatasets();
    const crd = this.route.snapshot.params['crd'];
    const plantationTable = this.route.snapshot.params['plantationTable'];

    if (this.hasRouteParams) {
      this.crdStateService.crd = crd;
      this.crdStateService.plantationTable = plantationTable;
      this.crdStateService.getRiskProperties();
      return;
    }

    // Both the org id and the remembered choice come from user info, so wait
    // for it to settle before choosing which dataset to open.
    combineLatest([
      this.crdStateService.datasets$.pipe(
        filter((datasets): datasets is Dataset[] => !!datasets?.length)
      ),
      // `merge`, not `race`: `race` forwards a source's completion, so a gate
      // that completes without passing the filter would cancel the timeout with
      // it. `merge` keeps the timer alive until one of them emits.
      merge(
        this.userStateService.userInfoLoaded$.pipe(filter(Boolean)),
        timer(USER_INFO_GATE_TIMEOUT_MS)
      ).pipe(take(1))
    ])
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(([datasets]) => {
        const dataset = resolveDefaultDataset(
          datasets,
          this.userStateService.currentCompanyId,
          this.datasetPreferenceService.getRemembered()
        );

        if (!dataset) {
          return;
        }

        this.crdStateService.setSelectedDataset(dataset);
        this.crdStateService.getRiskProperties();
      });
  }

  initializeToken() {
    if (this.canAssignToken) {
      this.authService.userInfoToken =
        this.route.snapshot.params['token'] || null;
    }
  }

  initializePendo() {
    this.userStateService.userInfo$
      .pipe(
        filter((userInfo) => !!userInfo),
        take(1)
      )
      .subscribe((userInfo) => {
        window.pendo?.initialize({
          visitor: {
            id: userInfo?.contact?.email,
            email: userInfo?.contact?.email,
            user_name: `${userInfo?.contact?.first_name} ${userInfo?.contact?.last_name}`
          },
          account: {
            id: userInfo?.company?.id?.toString(),
            name: userInfo?.company?.name
          }
        });
      });
  }

  initializePosthogUser() {
    this.userStateService.userInfo$
      .pipe(
        filter((userInfo): userInfo is UserInfo => !!userInfo),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((userInfo) => {
        const fullName = `${userInfo.contact?.first_name ?? ''} ${
          userInfo.contact?.last_name ?? ''
        }`.trim();
        this.posthogService.identifyUser(
          userInfo.id ?? userInfo.contact?.email,
          {
            email: userInfo.contact?.email,
            name: fullName || undefined,
            // The tenant as a person property too: person-targeted flag
            // conditions can't see groups, and the platform's Tenant settings
            // "me" scope is per tenant.
            hcp_company_id: userInfo.company.id?.toString()
          }
        );
        this.posthogService.setCompanyGroup(
          userInfo.company.id,
          userInfo.company.name
        );
      });
  }

  initializeSentryUser() {
    this.userStateService.userInfo$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((userInfo) => {
        Sentry.setUser(
          userInfo
            ? {
                id: userInfo.id?.toString(),
                email: userInfo.contact?.email,
                company_id: userInfo.company?.id,
                company_name: userInfo.company?.name,
                company_type: userInfo.company?.type
              }
            : null
        );
      });
  }
}
